import { useCallback, useEffect } from 'react';

import { useHelpChatSlice } from './useHelpChatSlice';
import { useIntercomInstance } from './useIntercomInstance';

export const useShowHideIntercomLauncher = (options: {
  hideId: string;
  shouldHide: boolean;
  errorHandling?: boolean;
}) => {
  const intercom = useIntercomInstance();
  const { state, actions } = useHelpChatSlice();
  // Use a stable boolean so effects don't rerun on every state object identity change.
  const hasSliceState = !!state;
  const { showLauncher, hideLauncher } = actions;

  const hide = useCallback(
    (id: string) => {
      hideLauncher({ launcherHideId: id });
    },
    // Excluding hideLauncher because it's unstable due to being recreated on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const show = useCallback(
    (id: string) => {
      showLauncher({ launcherHideId: id });
    },
    // Excluding showLauncher because it's unstable due to being recreated on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Update Intercom launcher visibility based on state changes
  useEffect(() => {
    if (!state) {
      if (options.errorHandling !== false) {
        throw new Error(
          'HelpChatSlice state is undefined. Ensure that the app is passing the slice state to createHcReduxStore() function',
        );
      }
      return;
    }
    if (intercom) {
      intercom('update', {
        hide_default_launcher: state.launcherHideId ? true : false,
      });
      // Allows opting out of error handling for certain implementations where intercom may or may not be available
      // depending on the context in which the hook/component is used
    } else if (options.errorHandling !== false) {
      throw new Error(
        `Attempting to update Intercom launcher while it's not available`,
      );
    }
    // Leaving `state` out of deps to avoid unnecessary updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.launcherHideId, intercom, options.errorHandling]);

  // Hide the Intercom launcher when configured to hide
  useEffect(() => {
    if (!hasSliceState) {
      return;
    }

    if (options.shouldHide) {
      hide(options.hideId);
    }
    return () => {
      show(options.hideId);
    };
  }, [hasSliceState, options.shouldHide, options.hideId, show, hide]);
};
